/**
 * Location Services Utility
 * Provides utility functions for location-related operations
 */

/**
 * Calculate distance between two coordinates using Haversine formula
 * @param {Object} coord1 - First coordinate {lat, lng}
 * @param {Object} coord2 - Second coordinate {lat, lng}
 * @returns {number} Distance in kilometers
 */
export const calculateDistance = (coord1, coord2) => {
  if (
    !coord1 ||
    !coord2 ||
    !coord1.lat ||
    !coord1.lng ||
    !coord2.lat ||
    !coord2.lng
  ) {
    return null;
  }

  const R = 6371; // Radius of the Earth in kilometers
  const dLat = deg2rad(coord2.lat - coord1.lat);
  const dLng = deg2rad(coord2.lng - coord1.lng);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(coord1.lat)) *
      Math.cos(deg2rad(coord2.lat)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c; // Distance in kilometers
  // Return distance with higher precision (3 decimal places) so very small
  // distances don't collapse to 0.0 when rounded. This keeps downstream
  // checks that rely on truthiness from treating 0 as "invalid" from
  // accidentally rounding small values to zero.
  return Math.round(distance * 1000) / 1000; // Round to 3 decimal places
};

/**
 * Convert degrees to radians
 * @param {number} deg - Degrees
 * @returns {number} Radians
 */
const deg2rad = (deg) => {
  return deg * (Math.PI / 180);
};

/**
 * Format distance for display
 * @param {number} distance - Distance in kilometers
 * @param {string} unit - Unit preference ('km', 'mi', or 'auto')
 * @returns {string} Formatted distance string
 */
export const formatDistance = (distance, unit = 'auto') => {
  if (!distance && distance !== 0) return 'Distance unavailable';

  if (unit === 'mi' || (unit === 'auto' && distance > 1.6)) {
    const miles = distance * 0.621371;
    return miles >= 1
      ? `${Math.round(miles * 10) / 10} mi`
      : `${Math.round(miles * 100) / 100} mi`;
  }

  return distance >= 1
    ? `${Math.round(distance * 10) / 10} km`
    : `${Math.round(distance * 1000)} m`;
};

/**
 * Get location status message based on location method
 * @param {Object} location - Location object with method property
 * @returns {string} Status message
 */
export const getLocationStatusMessage = (location) => {
  if (!location) return 'Location not available';

  const method = location.method;
  switch (method) {
    case 'GPS':
      return 'Precise GPS location';
    case 'IP':
      return `IP-based location: ${location.city}, ${location.country}`;
    case 'Timezone':
      return `Timezone-based location: ${location.timezone} region`;
    case 'Manual':
      return 'Manual address location';
    default:
      return 'Approximate location';
  }
};

/**
 * Check if location is precise enough for accurate distance calculations
 * @param {Object} location - Location object
 * @returns {boolean} Whether location is precise
 */
export const isLocationPrecise = (location) => {
  return location && location.method === 'GPS';
};

/**
 * Get Google Maps directions URL
 * @param {Object} from - Starting coordinate {lat, lng}
 * @param {Object} to - Destination coordinate {lat, lng}
 * @returns {string} Google Maps URL
 */
export const getDirectionsUrl = (from, to) => {
  if (!to || !to.lat || !to.lng) {
    return '#';
  }

  const destination = `${to.lat},${to.lng}`;

  if (from && from.lat && from.lng) {
    const origin = `${from.lat},${from.lng}`;
    return `https://www.google.com/maps/dir/${origin}/${destination}`;
  }

  return `https://www.google.com/maps/dir//${destination}`;
};

/**
 * Get Google Maps embed URL with marker
 * @param {Object} location - Location coordinate {lat, lng}
 * @param {number} zoom - Zoom level (default: 16)
 * @returns {string} Google Maps embed URL
 */
export const getMapEmbedUrl = (location, zoom = 16) => {
  if (!location || !location.lat || !location.lng) {
    return null;
  }

  return `https://www.google.com/maps?q=${location.lat},${location.lng}&z=${zoom}&output=embed`;
};

/**
 * Validate coordinate object
 * @param {Object} coord - Coordinate object
 * @returns {boolean} Whether coordinate is valid
 */
export const isValidCoordinate = (coord) => {
  return (
    coord &&
    typeof coord.lat === 'number' &&
    typeof coord.lng === 'number' &&
    coord.lat >= -90 &&
    coord.lat <= 90 &&
    coord.lng >= -180 &&
    coord.lng <= 180
  );
};

/**
 * Sort stores by distance from user location
 * @param {Array} stores - Array of store objects
 * @param {Object} userLocation - User location {lat, lng}
 * @returns {Array} Sorted stores with distance property
 */
export const sortStoresByDistance = (stores, userLocation) => {
  if (!stores || !userLocation) return stores;

  return stores
    .map((store) => {
      const storeLocation = store._geoloc || store.address;
      const distance = calculateDistance(userLocation, storeLocation);
      return { ...store, distance };
    })
    .sort((a, b) => {
      if (a.distance === null && b.distance === null) return 0;
      if (a.distance === null) return 1;
      if (b.distance === null) return -1;
      return a.distance - b.distance;
    });
};

export default {
  calculateDistance,
  formatDistance,
  getLocationStatusMessage,
  isLocationPrecise,
  getDirectionsUrl,
  getMapEmbedUrl,
  isValidCoordinate,
  sortStoresByDistance,
};
